// Shared logic for the "methodology break" divider column used by the
// brand-performance data-download tables.
//
// When data before and after a certain period is not comparable (e.g. a
// methodology change), the backend flags the break by returning a `cuts`
// array on every row (single-market selections only — the guard lives in the
// BE). Each entry is the exact date-column key of the LAST comparable
// (pre-change) column, so the divider is inserted immediately AFTER it (e.g.
// a cut between Q2 and Q3 is defined as "Q2"). The empty column carries no
// data; it just renders as a visual gap.

// Shared CSS class targeted by the divider styling in tables.scss.
export const METHODOLOGY_BREAK_CLASS = "methodology_break";

// Reads the cut keys off the table response. All rows share the same value,
// so the first row is authoritative.
export const getCuts = (data: any): string[] =>
  Array.isArray(data) && data.length > 0 && Array.isArray(data[0]?.cuts) ? data[0].cuts : [];

const makeBreakColumn = (cutKey: string) => ({
  id: `methodology_break_${cutKey}`,
  className: METHODOLOGY_BREAK_CLASS,
  Header: "",
  accessor: () => "",
  disableSortBy: true,
  Cell: () => "",
});

// Returns a copy of `dynamicColumns` with an empty divider column spliced in
// after each cut column. `cuts` values must match a `dateKeys` entry verbatim
// (the last pre-change column); unknown keys are ignored. Splicing happens
// right-to-left so earlier insertions don't shift the indices of cuts still to
// be placed.
export const withMethodologyBreaks = (dynamicColumns: any[], dateKeys: string[], cuts: string[]): any[] => {
  if (!cuts?.length) return dynamicColumns;

  const cutIndices = Array.from(new Set(cuts))
    .map((cutKey) => dateKeys.indexOf(cutKey))
    .filter((idx) => idx >= 0)
    .sort((a, b) => b - a);

  const columns = [...dynamicColumns];
  cutIndices.forEach((idx) => {
    // insert the divider immediately after the last comparable column
    columns.splice(idx + 1, 0, makeBreakColumn(dateKeys[idx]));
  });
  return columns;
};
